import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { copyEmbedUrl, isCopyBot, type CopyBlock, type CopyVariant } from "@/lib/abVariant";

export const ALERT_HEADLINE = "Get an email when a peptide's status changes";
export const ALERT_SUB = "We email you when the FDA or HHS makes a move on a peptide you care about. Every email links the official source. No spam, no sales.";
export const SUBSCRIBED_KEY = "pxSubscribed";
const FORM_URL = "https://subscribe-forms.beehiiv.com/v3/forms/81302ada-475a-49b2-88f3-7a0bffe27668";

export type AlertVariant = "hero" | "inline" | "sticky" | "footer";

interface AlertSignupProps {
  variant?: AlertVariant;
  /** Compound slug the signup came from; sent to beehiiv as the UTM campaign. */
  peptide?: string;
  source?: string;
  id?: string;
  headline?: string;
  description?: string;
  experiment?: { block: CopyBlock; variant: CopyVariant; ready: boolean };
  /** Replaces the default privacy line under the form. */
  footer?: string;
}

export function markSubscribed() {
  try { window.localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch { /* storage unavailable */ }
  window.dispatchEvent(new Event("px-subscribed"));
}

export function isSubscribed() {
  try { return window.localStorage.getItem(SUBSCRIBED_KEY) === "1"; } catch { return false; }
}

/** beehiiv owns the form inside the iframe; we only listen for its success message, if it sends one. */
function useBeehiivSuccess() {
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!/beehiiv\.com$/.test(new URL(e.origin).hostname)) return;
      const text = typeof e.data === "string" ? e.data : JSON.stringify(e.data ?? "");
      if (/success|subscribed/i.test(text)) markSubscribed();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
}

export default function AlertSignup({ variant = "inline", peptide, source = "peptidex", id, headline = ALERT_HEADLINE, description = ALERT_SUB, experiment, footer }: AlertSignupProps) {
  useBeehiivSuccess();
  const [subscribed, setSubscribed] = useState(false);
  useEffect(() => {
    setSubscribed(isSubscribed());
    const on = () => setSubscribed(true);
    window.addEventListener("px-subscribed", on);
    return () => window.removeEventListener("px-subscribed", on);
  }, []);
  const params = new URLSearchParams({ utm_source: source, utm_medium: "embed" });
  if (peptide) params.set("utm_campaign", peptide);
  const hero = variant === "hero";
  const compact = variant === "sticky";
  const currentSrc = `${FORM_URL}?${params.toString()}`;
  const src = experiment?.ready && !isCopyBot() ? copyEmbedUrl(currentSrc, experiment.block, experiment.variant) : currentSrc;
  return (
    <section id={id} data-alert-signup={variant} data-ab-variant={experiment?.variant} className={hero ? "alert-signup border-l-4 border-primary bg-accent px-4 py-4 sm:px-6" : compact ? "alert-signup" : "alert-signup my-10 terminal-card p-5 sm:p-8"}>
      {(headline || footer === undefined) && <h2 className="text-2xl font-bold text-foreground mb-2 pr-10">{headline}</h2>}
      {(description || footer === undefined) && <p className="text-sm leading-relaxed mb-4 text-muted-foreground">{description}</p>}
      {subscribed ? (
        <p role="status" className="text-base font-semibold text-primary">You're in. Check your inbox.</p>
      ) : (
        <div className={hero ? "reta-embed-window" : "reta-embed-full"}>
          <iframe
            src={src}
            data-testid="beehiiv-embed"
            frameBorder="0"
            scrolling="no"
            loading={hero || compact ? "eager" : "lazy"}
            className={hero ? "reta-embed reta-embed-hero" : "reta-embed"}
            title="PeptideX alerts signup"
          />
        </div>
      )}
      {footer !== undefined ? <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{footer}</p> : <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
        We don't collect health information. Unsubscribe anytime.{" "}
        <Link to="/privacy-policy" className="text-primary hover:underline">Privacy policy</Link>
      </p>}
    </section>
  );
}
